import { useKpisPerimeter, type FilterNameType, type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { findHoldings } from '#domain/core/filter/filter.query';
import { transformFilterFromSearch } from '#domain/core/filter/filter.utilities';
import { useUserStore } from '#stores/user.store';
import { type AllFilterType, type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, unref, type Ref } from 'vue';

interface UseHoldingDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
  filterType: AllFilterType;
  filterName: FilterNameType;
}

/**
 * @deprecated Use the new composable with the new permissions management.
 * TODO: [PM] Remove this method and the related code once the new route is implemented and used in the front
 */
export function useHoldingsData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy,
  filterType,
  filterName
}: UseHoldingDataProps) {
  const {
    filters: { selectedFiltersTree }
  } = useKpisPerimeter();
  const user = useUserStore();
  const { isAnonymized } = storeToRefs(user);

  const currentHoldingFilters = computed(() => {
    return Object.entries(selectedFiltersTree.value[filterName])
      .filter(([, state]) => state.state === 'checked' && state.filterType === filterType)
      .map(([filter]) => filter);
  });

  const {
    data: holdingData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [findHoldings.name, debouncedSearch, currentHoldingFilters, taxonomy],
    queryFn: async () => {
      return findHoldings({
        orNodeIdIn: currentHoldingFilters.value,
        search: debouncedSearch.value,
        limit: 100,
        taxonomy: unref(taxonomy)
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsHoldings = computed(() => {
    if (!holdingData.value || !enabled.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: isAnonymized.value
        ? transformFilterFromSearch(holdingData.value, 'Holding {level} - ******')
        : holdingData.value,
      defaultFilters: isAnonymized.value
        ? transformFilterFromSearch(holdingData.value, 'Holding {level} - ******')
        : holdingData.value,
      selectedFilters: checkedItemsState.value[filterName]
    });
  });

  const isLoadingHoldings = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsHoldings, isLoadingHoldings };
}
